import React, { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { toast } from "sonner";
import {
  Plus, Pencil, Trash2, HelpCircle, CheckCheck, Clock,
  MessageCircleQuestion, Building2,
} from "lucide-react";
import { authHeaders, API, formatApiErrorDetail } from "./api";

const PRIORITIES = ["Low", "Medium", "High"];
const DEFAULT_CATEGORIES = ["General", "Income", "Deposit", "Property", "Credit", "ID / Address", "Legal"];
const STATUSES = [
  { v: "pending", label: "Pending" },
  { v: "answered", label: "Answered" },
  { v: "resolved", label: "Resolved" },
];

const priorityStyle = (p) =>
  p === "High" ? "bg-red-50 text-red-700 border-red-200"
  : p === "Low" ? "bg-cream-3 text-forest/70 border-forest/15"
  : "bg-amber-50 text-amber-800 border-amber-200";

const statusStyle = (s) =>
  s === "resolved" ? "bg-forest text-cream border-forest"
  : s === "answered" ? "bg-emerald-50 text-emerald-700 border-emerald-200"
  : "bg-amber-50 text-amber-800 border-amber-200";

const statusLabel = (s) => STATUSES.find((x) => x.v === s)?.label || s;

const StatBox = ({ label, count, icon: Icon, tone }) => {
  const toneCls = {
    gold: "border-amber-200 bg-amber-50 text-amber-800",
    green: "border-emerald-200 bg-emerald-50 text-emerald-700",
    forest: "border-forest/15 bg-forest text-cream",
    muted: "border-forest/15 bg-cream-3 text-forest/70",
  }[tone];
  return (
    <div data-testid={`qa-stat-${label.toLowerCase()}`} className={`border rounded-lg p-3 ${toneCls}`}>
      <div className="flex items-center justify-between">
        <span className="text-[10.5px] tracking-[0.18em] uppercase font-bold opacity-80">{label}</span>
        <Icon size={14} strokeWidth={1.8} />
      </div>
      <p className="font-serif text-2xl mt-1 tabular-nums leading-none">{count}</p>
    </div>
  );
};

const emptyForm = () => ({
  lender_name: "", question: "", answer: "", category: "General",
  priority: "Medium", status: "pending", application_id: "",
});

const QAForm = ({ initial, apps, onSave, onCancel, saving }) => {
  const [form, setForm] = useState(initial || emptyForm());
  useEffect(() => { setForm(initial || emptyForm()); }, [initial]);
  const update = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));
  const submit = (e) => {
    e.preventDefault();
    if (!form.lender_name.trim()) { toast.error("Lender name is required"); return; }
    if (!form.question.trim()) { toast.error("Question text is required"); return; }
    onSave(form);
  };
  return (
    <form onSubmit={submit} className="bg-white border border-forest/15 rounded-lg p-5 space-y-4" data-testid="qa-form">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Lender <span className="text-red-600">*</span></span>
          <input type="text" value={form.lender_name} onChange={update("lender_name")} data-testid="qa-field-lender"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Application</span>
          <select value={form.application_id || ""} onChange={update("application_id")} data-testid="qa-field-application"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            <option value="">— (not linked to a specific application) —</option>
            {(apps || []).map((a) => (
              <option key={a.id} value={a.id}>
                {(a.type || "application").toString().toUpperCase()} · {a.customer_name || "Form"} {a.status === "signed" ? "(signed)" : ""}
              </option>
            ))}
          </select>
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Question <span className="text-red-600">*</span></span>
          <textarea rows={3} value={form.question} onChange={update("question")} data-testid="qa-field-question"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y" />
        </label>
        <label className="block sm:col-span-2">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Answer</span>
          <textarea rows={3} value={form.answer} onChange={update("answer")} data-testid="qa-field-answer"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none resize-y"
            placeholder="Record the customer's answer here. Changing status to Answered marks it as resolved later." />
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Category</span>
          <input list="qa-cat-list" type="text" value={form.category} onChange={update("category")} data-testid="qa-field-category"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none" />
          <datalist id="qa-cat-list">
            {DEFAULT_CATEGORIES.map((c) => <option key={c} value={c} />)}
          </datalist>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Priority</span>
          <select value={form.priority} onChange={update("priority")} data-testid="qa-field-priority"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {PRIORITIES.map((p) => <option key={p} value={p}>{p}</option>)}
          </select>
        </label>
        <label className="block">
          <span className="text-[11px] tracking-[0.14em] uppercase font-bold text-forest/70">Status</span>
          <select value={form.status} onChange={update("status")} data-testid="qa-field-status"
            className="mt-1 w-full bg-white border border-forest/15 px-3 py-2 text-sm focus:border-gold focus:outline-none">
            {STATUSES.map((s) => <option key={s.v} value={s.v}>{s.label}</option>)}
          </select>
        </label>
      </div>
      <div className="flex gap-2">
        <button type="submit" disabled={saving} data-testid="qa-save-btn" className="btn-gold disabled:opacity-50">
          {saving ? "Saving…" : (initial ? "Save changes" : "Add question")}
        </button>
        <button type="button" onClick={onCancel} data-testid="qa-cancel-btn" className="btn-ghost-dark">Cancel</button>
      </div>
    </form>
  );
};

export const CustomerLenderQA = ({ customerId, customerName }) => {
  const [data, setData] = useState({ questions: [], summary: { pending: 0, answered: 0, resolved: 0, total: 0 } });
  const [apps, setApps] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);
  const [saving, setSaving] = useState(false);

  const reload = () => {
    setLoading(true);
    Promise.all([
      axios.get(`${API}/admin/customers/${customerId}/lender-questions`, authHeaders()),
      axios.get(`${API}/admin/forms`, authHeaders()).catch(() => ({ data: [] })),
    ])
      .then(([q, f]) => {
        setData(q.data);
        setApps((f.data || []).filter((row) => row.customer_id === customerId));
      })
      .catch(() => toast.error("Could not load lender questions"))
      .finally(() => setLoading(false));
  };
  useEffect(reload, [customerId]);

  const filtered = useMemo(() => {
    const rows = data.questions || [];
    if (filter === "all") return rows;
    return rows.filter((q) => q.status === filter);
  }, [data.questions, filter]);

  const save = async (payload) => {
    setSaving(true);
    try {
      if (editing === "new") {
        await axios.post(`${API}/admin/customers/${customerId}/lender-questions`, payload, authHeaders());
        toast.success("Question added");
      } else {
        await axios.put(`${API}/admin/customers/${customerId}/lender-questions/${editing.id}`, payload, authHeaders());
        toast.success("Question updated");
      }
      setEditing(null);
      reload();
    } catch (e) {
      toast.error(formatApiErrorDetail(e.response?.data?.detail) || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const changeStatus = async (q, nextStatus) => {
    try {
      await axios.put(`${API}/admin/customers/${customerId}/lender-questions/${q.id}`,
        { ...q, status: nextStatus }, authHeaders());
      reload();
    } catch { toast.error("Could not change status"); }
  };

  const remove = async (q) => {
    if (!window.confirm("Delete this lender question?")) return;
    try {
      await axios.delete(`${API}/admin/customers/${customerId}/lender-questions/${q.id}`, authHeaders());
      toast.success("Question deleted");
      reload();
    } catch { toast.error("Could not delete"); }
  };

  if (loading) return <p className="text-sage">Loading lender questions…</p>;

  const s = data.summary;

  return (
    <div className="space-y-5" data-testid="customer-lender-qa-panel">
      <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
        <StatBox label="Pending" count={s.pending} icon={Clock} tone="gold" />
        <StatBox label="Answered" count={s.answered} icon={MessageCircleQuestion} tone="green" />
        <StatBox label="Resolved" count={s.resolved} icon={CheckCheck} tone="forest" />
        <StatBox label="Total" count={s.total} icon={HelpCircle} tone="muted" />
      </div>

      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex gap-1.5 flex-wrap">
          {[
            { v: "all", label: "All" },
            ...STATUSES,
          ].map((f) => (
            <button key={f.v} type="button" onClick={() => setFilter(f.v)} data-testid={`qa-filter-${f.v}`}
              className={`px-3 py-1.5 text-[11.5px] font-semibold tracking-tight rounded-full border transition-colors ${
                filter === f.v ? "bg-forest text-cream border-forest" : "bg-white border-forest/20 text-forest/70 hover:border-forest"
              }`}>
              {f.label}
            </button>
          ))}
        </div>
        {!editing && (
          <button type="button" onClick={() => setEditing("new")} data-testid="qa-add-btn"
            className="inline-flex items-center gap-2 bg-forest hover:bg-forest-soft text-cream px-4 py-2.5 rounded text-[11px] font-bold tracking-[0.2em] uppercase transition-colors">
            <Plus size={14} strokeWidth={2.2} /> Add question
          </button>
        )}
      </div>

      {editing && (
        <QAForm
          initial={editing === "new" ? null : editing}
          apps={apps}
          onCancel={() => setEditing(null)}
          onSave={save}
          saving={saving}
        />
      )}

      {filtered.length === 0 ? (
        <div className="bg-white border border-forest/10 rounded-xl p-10 text-center" data-testid="qa-empty">
          <MessageCircleQuestion size={28} strokeWidth={1.4} className="mx-auto text-forest/30 mb-3" />
          <p className="font-serif text-lg text-forest mb-1">
            {filter === "all" ? `No lender questions yet for ${customerName}` : `No ${statusLabel(filter).toLowerCase()} questions`}
          </p>
          <p className="text-[13px] text-sage mb-5">
            Track every lender query here so nothing slips through the cracks.
          </p>
          {!editing && (
            <button type="button" onClick={() => setEditing("new")} data-testid="qa-empty-add"
              className="btn-gold inline-flex items-center gap-2">
              <Plus size={14} strokeWidth={2.2} /> Add first question
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-2.5">
          {filtered.map((q, i) => (
            <div key={q.id} data-testid={`qa-row-${i + 1}`}
              className="bg-white border border-forest/10 rounded-lg p-4">
              <div className="flex items-start justify-between gap-3 flex-wrap mb-2">
                <div className="flex items-center gap-2 flex-wrap min-w-0">
                  <span className="inline-flex items-center gap-1 text-[11px] font-bold tracking-tight text-forest">
                    <Building2 size={12} strokeWidth={1.8} className="text-gold-dark" /> {q.lender_name}
                  </span>
                  <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${statusStyle(q.status)}`}>{statusLabel(q.status)}</span>
                  <span className={`inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded ${priorityStyle(q.priority)}`}>{q.priority}</span>
                  {q.category && (
                    <span className="inline-flex items-center text-[10px] font-bold tracking-[0.1em] uppercase px-2 py-0.5 border rounded bg-cream-3 text-forest/70 border-forest/15">{q.category}</span>
                  )}
                </div>
                <div className="flex gap-1">
                  {q.status !== "resolved" && (
                    <button type="button" onClick={() => changeStatus(q, q.status === "pending" ? "answered" : "resolved")}
                      data-testid={`qa-advance-${i + 1}`}
                      title={q.status === "pending" ? "Mark Answered" : "Mark Resolved"}
                      className="p-1.5 text-emerald-700/80 hover:text-emerald-800 hover:bg-emerald-50 rounded">
                      <CheckCheck size={14} strokeWidth={1.8} />
                    </button>
                  )}
                  <button type="button" onClick={() => setEditing(q)} data-testid={`qa-edit-${i + 1}`}
                    className="p-1.5 text-forest/60 hover:text-forest hover:bg-cream-3 rounded" title="Edit">
                    <Pencil size={14} strokeWidth={1.8} />
                  </button>
                  <button type="button" onClick={() => remove(q)} data-testid={`qa-delete-${i + 1}`}
                    className="p-1.5 text-rose-500/80 hover:text-rose-700 hover:bg-rose-50 rounded" title="Delete">
                    <Trash2 size={14} strokeWidth={1.8} />
                  </button>
                </div>
              </div>
              <p className="text-forest whitespace-pre-wrap"><span className="font-semibold">Q:</span> {q.question}</p>
              {q.answer && (
                <p className="text-forest/80 whitespace-pre-wrap mt-2 pl-3 border-l-2 border-emerald-300">
                  <span className="font-semibold text-emerald-700">A:</span> {q.answer}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};
